import { createSelector } from "@ngrx/store";

import { isEqual } from 'lodash';
import { EventLocationTypes, eventTypeInfoMap, eventTypeInfoMapV2 } from "src/app/global.constants";
import { getJobLocation } from "src/app/jobs/jobs.util";
import { createConfigSelector } from "src/app/state/branding.store";

import { dayjs } from '../../../core/dayjs/dayjs';
import { EventStatuses } from '../../../schedules/dispatch/store/dispatch.reducer';
import { scheduleFeature } from '../../../schedules/store/schedules.reducer';
import { ExtendedCalendarEventForScheduleFragment } from '../../../services/full-calendar-helper.service';
import { convertSecondsToTimeUnit } from '../../../shared/seconds-to-time-unit.pipe';
import { jobToolFeature } from "../../job-tool.reducer";
import { calculateEventTime, EventInfoJobsV2, EventInfoLocationJobsV2 } from '../../jobsv2-events-helpers';

export const selectEventMissingLocations = createSelector(
  jobToolFeature.selectJob,
  jobToolFeature.selectEventToBook,
  (job, event) => {
      if (!event) {
          return [];
      }

      const eventTypeInfo = eventTypeInfoMapV2[event.type];

      const missingLocations = [];

      const requiredLocations = eventTypeInfo?.requiredLocations || [];

      for (const locationType of requiredLocations) {
          if (!getJobLocation(job, locationType)) {
              missingLocations.push(locationType);
          }
      }

      return missingLocations;
  }
);

export const selectEventsWithMissingLocations = createSelector(
  jobToolFeature.selectJob,
  (job) => {
    if (!job || !job.events) {
      return {};
    }

    const eventsWithMissingLocations: Record<string, string[]> = {};

    for (const event of job.events) {
      const eventTypeInfo = eventTypeInfoMapV2[event.type];

      // Find all missing locations for the current event
      const missingLocations = eventTypeInfo.requiredLocations.filter(
        (locationType) => !getJobLocation(job, locationType)
      );

      if (missingLocations.length > 0) {
        eventsWithMissingLocations[event.id] = missingLocations;
      }
    }

    return eventsWithMissingLocations;
  }
);

export const selectJobZone = createSelector(
  jobToolFeature.selectJob,
  (job) => {
    return job?.zone;
  }
);

const selectAllowedAssets = createSelector(
    scheduleFeature.selectAssets,
    jobToolFeature.selectEventToBook,
    jobToolFeature.selectIsBookingRestrictionDisabled,
    (assets = [], eventToBook, isBookingRestrictionDisabled) => {
      if (!eventToBook) return [];

      if (isBookingRestrictionDisabled) return assets;

      const assetTypes = eventTypeInfoMap[eventToBook.type]?.assetTypes;
      return assetTypes?.length ? assets.filter(a => assetTypes.includes(a.type)) : [];
    },
);

const selectPossibleAssets = createSelector(
    jobToolFeature.selectAvailableAssets,
    selectAllowedAssets,
    jobToolFeature.selectIsBookingRestrictionDisabled,
    (availableAssets, allAssets, restrictionsDisabled) =>
        restrictionsDisabled ? allAssets : availableAssets
);

export const selectHasUserModifiedTravelTimes = createSelector(
  jobToolFeature.selectUserModifiedTravelTimes,
  (userModifiedTravelTimes) =>
    userModifiedTravelTimes && Object.keys(userModifiedTravelTimes).length > 0
);

function getLocationArrivalAndDepartureTimes(locations: EventInfoLocationJobsV2[], selectedStartTime: number, timezone: string)
  : EventInfoLocationJobsV2[] {
    // The selectedStartTime represents the expected arrival time at the client's location (Start Location).
    // To determine the actual start time for the first location, we need to adjust it by subtracting
    // the dock travel time, accounting for both travel time and any additional offsets.

  let currentTime = (selectedStartTime || 0);

  const dockStart = locations.find((location) => location.type === EventLocationTypes.dockStart);
  if (dockStart) {
    currentTime = (selectedStartTime || 0)
                - (dockStart.travelTimeToNextLocation || 0)
                - (dockStart.travelTimeToNextLocationOffset || 0)
                - (dockStart.estimatedTimeAtLocation || 0);
  }

  return locations.map((location) => {
    const arrivalTime = currentTime;
    currentTime += (location.estimatedTimeAtLocation || 0);

    const departureTime = currentTime;

    const totalTravelTimeToNextLocation =
      (location.travelTimeToNextLocation || 0) +
      (location.travelTimeToNextLocationOffset || 0);
    currentTime += totalTravelTimeToNextLocation;

    const arrivalTimeDayJs = dayjs.tz(arrivalTime * 1000, timezone);
    const departureTimeDayJs = dayjs.tz(departureTime * 1000, timezone);

    return {
      ...location,
      arrivalTimeEpoch: arrivalTimeDayJs.unix(),
      departureTimeEpoch: departureTimeDayJs.unix(),
      arrivalTime: arrivalTimeDayJs.format("h:mm A"),
      departureTime: departureTimeDayJs.format("h:mm A"),
      totalTravelTimeToNextLocation,
    };
  });
}

export const selectEventItinerary = createSelector(
  jobToolFeature.selectJob,
  jobToolFeature.selectEventToBook,
  jobToolFeature.selectIncludeDockTravel,
  createConfigSelector('CALENDAREVENTS_ESTIMATELENGTH'),
  createConfigSelector('CALENDAREVENTS_VIRTUALESTIMATELENGTH'),
  jobToolFeature.selectSelectedStartTime,
  jobToolFeature.selectJobTimezone,
  jobToolFeature.selectUserModifiedTravelTimes,
  (job, event, includeDockTravel, estimatingLength, voseLength, selectedStartTime,
    timezone, userModifiedTravelTimes): EventInfoJobsV2 => {

    if (!job || !event) {
      return undefined;
    }

    const eventTimeFromConfig = {
      estimating: estimatingLength,
      virtualEstimate: voseLength,
    }

    const result = calculateEventTime(
      job,
      event.id,
      includeDockTravel,
      includeDockTravel,
      eventTimeFromConfig,
      userModifiedTravelTimes,
    );

    const locationsWithItinerary = getLocationArrivalAndDepartureTimes(result.locations, selectedStartTime, timezone);
    return { ...result, locations: locationsWithItinerary };
  }
);

export const selectEventItineraryLocations = createSelector(
  selectEventItinerary,
  (itinerary) => {
    return itinerary?.locations || [];
  }
);

export const selectTotalLocationTime = createSelector(
  selectEventItinerary,
  (itinerary) => {
    return itinerary?.totalLocationTime;
  }
);

export const selectTotalTravelTime = createSelector(
  selectEventItinerary,
  (itinerary) => {
    return itinerary?.totalTravelTime;
  },
);

export const selectTotalTime = createSelector(
  selectEventItinerary,
  (itinerary) => {
    return itinerary?.totalTime;
  },
);

export const selectItinerarySummary = createSelector(
  selectEventItinerary,
  (itinerary) => {
    const { totalLocationTime, totalTravelTime, totalTime } = itinerary || {};

    if (!totalTime) {
      return null;
    }

    // Minimum width for visibility
    const minWidth = 20;
    let laborTimeWidth = ((totalLocationTime / totalTime) * 100);
    let travelTimeWidth = 100 - laborTimeWidth;

    // Ensure minimum width for both bars
    if (laborTimeWidth < minWidth) {
      laborTimeWidth = minWidth;
      travelTimeWidth = 100 - laborTimeWidth;
    } else if (travelTimeWidth < minWidth) {
      travelTimeWidth = minWidth;
      laborTimeWidth = 100 - travelTimeWidth;
    }

    return {
      laborTimeWidth,
      travelTimeWidth,
      totalLocationTime: `Labor: ${convertSecondsToTimeUnit(totalLocationTime).trim()}`,
      totalTravelTime: `Travel: ${convertSecondsToTimeUnit(totalTravelTime).trim()}`,
      totalTime: `Total: ${convertSecondsToTimeUnit(totalTime)}`,
    };
  }
);

export const selectPlaceholderEvent = createSelector(
  jobToolFeature.selectEventToBook,
  jobToolFeature.selectSelectedStartTime,
  selectTotalTime,
  jobToolFeature.selectSelectedAssets,
  selectPossibleAssets,
  jobToolFeature.selectScheduleMode,
  selectEventItinerary,
  (event, selectedStartTime, totalTime, selectedAssetIds, assets, scheduleMode, itinerary): ExtendedCalendarEventForScheduleFragment => {
    if (!selectedStartTime || !totalTime || !event) {
      return event;
    }

    if (scheduleMode === 'view') {
      return event;
    }

    const itineraryLocations = itinerary?.locations || [];
    const eventLocations = itineraryLocations.length ? itineraryLocations : event?.locations;

    const startTime = itineraryLocations?.[0]?.arrivalTimeEpoch || selectedStartTime;
    // const dockStart = eventLocations.find((location) => location.type === EventLocationTypes.dockStart);
    // if (dockStart) {
    //   startTime = selectedStartTime - (dockStart.travelTimeToNextLocation || 0) - (dockStart.travelTimeToNextLocationOffset || 0)
    //   - (dockStart.estimatedTimeAtLocation || 0);
    // }

    const end = itineraryLocations?.[itineraryLocations.length - 1]?.departureTimeEpoch || startTime + totalTime;
    const selectedAssets = (selectedAssetIds || [])
      .map((id) => assets.find((a) => a.id === id))
      .filter(Boolean);

    const eventAssetIds = event?.assets?.map((a) => a.id) || [];
    const hasChanges = event.start !== startTime || event.end !== end
      || !isEqual(selectedAssetIds, eventAssetIds);
    const isNotBooked = event.status !== EventStatuses.Booked;

    return {
      ...event,
      start: startTime,
      end,
      assets: selectedAssets,
      eventIsPlaceholder: hasChanges || isNotBooked,
      locations: eventLocations,
    };
  },
)

export const selectEventsWithChanges = createSelector(
  scheduleFeature.selectEvents,
  selectPlaceholderEvent,
  (events, placeholderEvent) => {

    events = [...(events || [])];
    if (!placeholderEvent) { return events; }

    const index = events.findIndex((e) => e.id === placeholderEvent.id);
    if (index >= 0) {
      events[index] = placeholderEvent;
    } else {
      events.push(placeholderEvent);
    }

    return events;

  },
);

export const selectStartTimeInterval = createConfigSelector('FIND_STARTTIMEINTERVAL');
export const selectDefaultForIncludeDockTravel = createConfigSelector('CALENDAREVENTS_INCLUDEDOCKTRAVELTIME');
export const selectShowDockTravelTimeCheckbox = createConfigSelector('CALENDAREVENTS_DOCKTRAVELTIMECHECKBOX');

export const eventScheduleSelectors = {
    selectEventMissingLocations,
    selectEventsWithMissingLocations,
    selectJobZone,
    selectAllowedAssets,
    selectEventItinerary,
    selectTotalLocationTime,
    selectTotalTravelTime,
    selectTotalTime,
    selectStartTimeInterval,
    selectPossibleAssets,
    selectShowDockTravelTimeCheckbox,
    selectDefaultForIncludeDockTravel,
    selectHasUserModifiedTravelTimes,
    selectItinerarySummary,
}
